


import { Component, Vue, Prop } from 'vue-property-decorator';
import Toast from './display/toast.vue';
import { VolumeConversionUtils } from '@/common/VolumeConversionUtils';
import { VatModels, VatAttributes, VatType } from '@/common/EmptyDistanceUtils';

@Component({})
export default class EmptyDistanceGenerator extends Vue {
	private toast: Toast;

	constructor() {
		super();
		this.toast = new Toast();
	}

	public calculateVatAttributes(modelName: string): VatAttributes {
		if (modelName.includes('Crown')) {
			modelName = modelName + 'W';
		}
		const matchingModel = VatModels.get(modelName);
		if (matchingModel !== undefined) {
			// Convert to mm
			const slopeOffset = matchingModel.slopeOffset / 1000;
			const diameter = matchingModel.diameter / 1000;
			const radius = diameter / 2;
			const height = matchingModel.height / 1000;
			const ventDistance = matchingModel.ventDistance / 1000;
			return {
				slopeOffset,
				diameter,
				radius,
				height,
				ventDistance,
			};
		} else {
			this.toast.makeToast('No matching vat model', 'Error', 'danger', false);
			throw new Error('No matching vat model');
		}
	}

	public convertVolumeToDistance(
		model: string, // E.g. Butterfly:3700
		vatAttributes: VatAttributes,
		offset: number,
		emptyVolume: number,
	): number | undefined {
		const vatType = model.split(':')[0] as VatType;
		const distance = (vatAttributes.height + offset) * 1000;
		switch (vatType) {
			case VatType.FONTERRA: {
				const conversionResult = this.runConversion(
					distance,
					VolumeConversionUtils.fonterraVolumeFromDistance,
					[
						offset,
						vatAttributes.slopeOffset,
						vatAttributes.diameter,
						vatAttributes.radius,
						vatAttributes.height,
						vatAttributes.ventDistance,
					],
				);
				return this.findMatchingDistance(
					emptyVolume,
					conversionResult.volumes,
					conversionResult.distances,
				);
			}
			case VatType.BUTTERFLY: {
				const conversionResult = this.runConversion(
					distance,
					VolumeConversionUtils.butterflyVolumeFromDistance,
					[offset, vatAttributes.height, vatAttributes.slopeOffset, vatAttributes.radius],
				);
				return this.findMatchingDistance(
					emptyVolume,
					conversionResult.volumes,
					conversionResult.distances,
				);
			}
			case VatType.LONGVELD: {
				const conversionResult = this.runConversion(
					distance,
					VolumeConversionUtils.longveldVolumeFromDistance,
					[offset, vatAttributes.height, vatAttributes.slopeOffset, vatAttributes.radius],
				);
				return this.findMatchingDistance(
					emptyVolume,
					conversionResult.volumes,
					conversionResult.distances,
				);
			}
			case VatType.SPHERICAL: {
				const conversionResult = this.runConversion(
					distance,
					VolumeConversionUtils.sphericalVolumeFromDistance,
					[offset, vatAttributes.diameter],
				);
				return this.findMatchingDistance(
					emptyVolume,
					conversionResult.volumes,
					conversionResult.distances,
				);
			}
			case VatType.CROWN: {
				const crownDistance = 3330; // max distance for a crown 21000W - approximation given volumes close to 0
				const conversionResult = this.runConversion(
					crownDistance,
					VolumeConversionUtils.crownVolumeFromDistance,
					[offset],
				);
				return this.findMatchingDistance(
					emptyVolume,
					conversionResult.volumes,
					conversionResult.distances,
				);
			}
		}
	}

	// Given the maximum possible distance (near full vat), generate all the possible distances and their corresponding values (roughly).
	private runConversion(
		distance: number,
		conversionFunction: (...args: number[]) => string,
		conversionParameters: number[],
	): { distances: number[]; volumes: string[] } {
		const distances = [];
		const volumes = [];
		while (distance - 5 > 0) {
			distances.push(distance);
			volumes.push(conversionFunction(distance, ...conversionParameters));
			distance -= 5;
		}
		return { distances, volumes };
	}

	// Given all the volumes and distances, find the closest volume to the one provided (user input), and get its corresponding distance. (As per spreadsheet implementation)
	private findMatchingDistance(
		emptyVolume: number,
		volumes: string[],
		distances: number[],
	): number | undefined {
		const closestEmptyVolume = volumes.find(
			(volume: string) => parseInt(volume, 10) >= emptyVolume,
		);
		if (closestEmptyVolume) {
			const index = volumes.indexOf(closestEmptyVolume);
			return distances[index];
		}
	}
}
